import type { StateStorage } from 'zustand/middleware';

// Storage can be unavailable (private mode, blocked site data); state then
// lasts for the session only.
export const safeStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      /* session-only */
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      /* session-only */
    }
  },
};

function read(storage: () => Storage, name: string) {
  try {
    return storage().getItem(name);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, name: string, value: string) {
  try {
    storage().setItem(name, value);
  } catch {
    /* session-only */
  }
}

const tab = () => window.sessionStorage;
const shared = () => window.localStorage;
const inView = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * Each tab keeps its own copy, so a reload stays in the tab's workspace; a new tab starts from
 * the tab seen last, whose copy is the shared one.
 */
export function tabStorage(name: string): StateStorage {
  if (typeof document !== 'undefined')
    document.addEventListener('visibilitychange', () => {
      const value = inView() ? read(tab, name) : null;
      if (value) write(shared, name, value);
    });
  return {
    getItem: (key) => read(tab, key) ?? read(shared, key),
    setItem: (key, value) => {
      write(tab, key, value);
      if (inView()) write(shared, key, value);
    },
    removeItem: (key) => {
      for (const storage of [tab, shared]) {
        try {
          storage().removeItem(key);
        } catch {
          /* session-only */
        }
      }
    },
  };
}
